import '../FormConfirmation/FormConfirmation.css'
import { useEffect, useState } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import PersonalInfo from "./component/PersonalInfo";
import ScheduleSelector from "./component/ScheduleSelector";
import PriceSummary from "./component/PriceSummary";
import { PACKAGE_TYPES } from "./packageTypes";
import { useVoucher } from "./hooks/useVoucher";
import NotesSection from "./component/NotesSection";
import CommitmentSection from "./component/CommitmentSection";
import StatementLetterSection from "./component/StatementLetterSection";
import Swal from "sweetalert2";
import API_URL from "../API";
import { calculateSchedulePricing } from "./utils/schedulePricing.utils";
import { formatCity } from "./utils/formatCity";
import { generateRegistrationPdf } from "./utils/pdf/generateRegistrationPdf";
import { generateRegistrationDocx } from "./utils/docx/generateRegistrationDocx";
import { thisYear, nextYear } from '../Footer/footer';

const FormConfirmation = () => {

  const methods = useForm({
    defaultValues: {
      schedules: [],
      confirm: false
    },
  });

  const {
    control,
    register,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "schedules",
  });

  const voucherInput = watch("voucherCode");
  const { voucherValid } = useVoucher();
  const confirmCheckbox = watch('confirm');
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    if (!confirmCheckbox) {
      methods.setValue("signedDocument", null);
    }
  }, [confirmCheckbox, methods]);

  const validateSchedules = (data) => {
    const schedules = data.schedules || [];
    const activeSchedules = schedules.filter((item) => item && item.packageType);

    if (activeSchedules.length === 0) {
      setError("schedules", {
        type: "manual",
        message: "Please select at least one event schedule.",
      });
      return false;
    }

    for (const item of activeSchedules) {
      if (item.packageType !== PACKAGE_TYPES.PLATINUM) {
        if (!item.cities || item.cities.length === 0) {
          setError("schedules", {
            type: "manual",
            message: `Please select at least one city.`,
          });
          return false;
        }
      }
    }

    clearErrors("schedules");
    return true;
  };

  const validateCityPricing = (data) => {

    const selectedCities = data.selectedCities || [];
    const selectedTrialCities = data.selectedTrialCities || [];

    if (data.price === "1700" && selectedCities.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No city selected",
        text: "Please select at least one city for Regular Package (AUD 1700 / city)."
      });
      return false;
    }

    if (data.price_trial === "1200" && selectedTrialCities.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No city selected",
        text: "Please select at least one city for Trial Package (AUD 1200 / city)."
      });
      return false;
    }

    return true;
  };

  const formatCitiesForPDF = (cities = []) => {
    if (!Array.isArray(cities) || cities.length === 0) {
      return "";
    }

    return cities.map(formatCity).join(", ");
  };

  const generatePDF = () => {
    generateRegistrationPdf({
      methods,
      voucherValid,
      calculateSchedulePricing,
      formatCitiesForPDF,
    });
  };

  const generateDOCX = () => {
    generateRegistrationDocx({
      methods,
      voucherValid,
      calculateSchedulePricing,
      formatCities: formatCitiesForPDF,
    });
  };

  const onSubmit = async (data) => {
    setBtnLoading(true);

    if (!validateSchedules(data)) {
      setBtnLoading(false);
      return;
    }

    if (!validateCityPricing(data)) {
      setBtnLoading(false);
      return;
    }

    try {

      const {
        originalTotal,
        discountedTotal,
        normalizedSchedules
      } = calculateSchedulePricing(data.schedules, voucherValid);

      const formDataObj = new FormData();

      let guestsData = [];
      if (data.invitation === '1' && data.guests) {
        guestsData = data.guests.filter(guest =>
          guest && guest.fullName && guest.email && guest.phone && guest.institution
        );
      }

      const jsonData = {
        ...data,
        notes: data.notes || null,
        schedules: normalizedSchedules,
        priceBeforeDiscount: originalTotal,
        priceAfterDiscount: discountedTotal,
        voucherCode: data.voucherCode || '',
        voucherApplied: voucherValid,
        codeVoucher: voucherValid ? voucherInput?.toUpperCase() : null,
        guests: guestsData
      };

      formDataObj.append('data', JSON.stringify(jsonData));
      // console.log("VALID DATA", JSON.stringify(jsonData, null, 2));      

      if (data.signedDocument && data.signedDocument[0]) {
        formDataObj.append('signedDocument', data.signedDocument[0]);
      }

      const response = await API_URL.eventUrl.post('form-confirmation-edufest', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Submission Successful',
          text: 'Your form has been submitted!'
        }).then(() => {
          window.location.reload();
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error("Submission error:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Submission failed';
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: errorMessage
      });
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="confirmation-theme-page bg-light">
      <div style={{ height: 50 }}></div>
      <div className="container">

        <div className="title-form">
          <h1 className="" >Confirmation of Participant</h1>
          <p className="title-2" >NIEC World Education Festival {thisYear} - {nextYear}</p>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-7">
                <PersonalInfo
                  register={register}
                  errors={errors}
                  control={control}
                />

                <ScheduleSelector
                  fields={fields}
                  append={append}
                  remove={remove}
                />

                <NotesSection
                  register={register}
                />

              </div>

              <div className="col-md-5 ">
                <PriceSummary
                  control={methods.control}
                  voucherValid={voucherValid}
                  btn={btnLoading}
                >
                  <CommitmentSection
                    register={register}
                    errors={errors}
                    watch={watch}
                  >
                    {confirmCheckbox && (
                      <StatementLetterSection
                        register={register}
                        errors={errors}
                        onDownloadPdf={generatePDF}
                        onDownloadDocx={generateDOCX}
                        selectedFile={watch("signedDocument")?.[0]}
                      />
                    )}
                  </CommitmentSection>
                </PriceSummary>
              </div>
            </div>

          </form>
        </FormProvider>

      </div>
    </div>
  );
};

export default FormConfirmation;
